import React, { useEffect, useRef } from "react";
import { Tabs, Button, Space, Card, Row, Col, Popconfirm } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Location } from "../hooks/useFinanceData";
import type {
  SupplierCustomValue,
  DailyRevenueSupplierExpense,
  SupplierDefaultValue,
} from "../../../utils/api/supplierExpensesAPI";
import {
  updateCustomValuePaymentStatus,
  updateDailyRevenueExpensePaymentStatus,
} from "../../../utils/api/supplierExpensesAPI";
import { PaymentStatusBadge } from "./PaymentStatusBadge";

interface SupplierDetailsProps {
  supplierId: string;
  supplierName: string;
  categoryId: string;
  locations: Location[];
  defaultValues: SupplierDefaultValue[];
  customValues: SupplierCustomValue[];
  dailyRevenueExpenses: DailyRevenueSupplierExpense[];
  canUpdateMatrix: boolean;
  onAddCustomValue: (supplierData: {
    supplierId: string;
    supplierName: string;
    categoryId: string;
    locationId: string;
  }) => void;
  onEditCustomValue: (
    customValue: any,
    supplierData: {
      supplierId: string;
      supplierName: string;
      categoryId: string;
      locationId: string;
    },
  ) => void;
  onDeleteCustomValue: (id: string) => void;
  onRefresh: () => Promise<void>;
  highlightCustomValueId?: string;
}

export const SupplierDetails: React.FC<SupplierDetailsProps> = ({
  supplierId,
  supplierName,
  categoryId,
  locations,
  defaultValues,
  customValues,
  dailyRevenueExpenses,
  canUpdateMatrix,
  onAddCustomValue,
  onEditCustomValue,
  onDeleteCustomValue,
  onRefresh,
  highlightCustomValueId,
}) => {
  const { t } = useTranslation();
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlightCustomValueId || !highlightRef.current) return;
    const timer = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightCustomValueId]);

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("sr-RS", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Build tabs for each location that has expenses
  const locationTabs = locations
    .map((location) => {
      // Get ALL default values for multi-month periods (sum them)
      const matchingDefaultValues = defaultValues.filter(
        (dv) =>
          dv.supplierId === supplierId &&
          dv.categoryId === categoryId &&
          dv.locationId === location._id,
      );

      // Get ALL custom values for this supplier+location
      const allLocationCustomValues = customValues.filter(
        (cv) =>
          cv.supplierId === supplierId &&
          (cv.locationId === location._id ||
            (cv.locationId as any)?._id === location._id),
      );
      // Split into generated fixed expenses and manual one-off invoices
      const locationFixedExpenses = allLocationCustomValues.filter(
        (cv) => cv.type === "fixed",
      );
      const locationCustomValues = allLocationCustomValues.filter(
        (cv) => cv.type !== "fixed",
      );

      // Get daily revenue expenses
      const locationDailyExpenses = dailyRevenueExpenses.filter(
        (exp) =>
          exp.supplierId === supplierId &&
          (exp.locationId === location._id ||
            (exp.locationId as any)?._id === location._id),
      );

      // Calculate totals
      let totalRsd = 0;
      let totalEur = 0;

      // Sum all default values (for multi-month periods)
      matchingDefaultValues.forEach((dv) => {
        totalRsd += dv.defaultValue || 0;
      });

      locationCustomValues.forEach((cv) => {
        if (cv.currency === "RSD") totalRsd += cv.amount || 0;
        else if (cv.currency === "EUR") totalEur += cv.amount || 0;
      });

      locationDailyExpenses.forEach((exp) => {
        totalRsd += exp.amount || 0; // Daily revenue expenses are always in RSD
      });

      // Skip location if no expenses
      if (
        matchingDefaultValues.length === 0 &&
        locationFixedExpenses.length === 0 &&
        locationCustomValues.length === 0 &&
        locationDailyExpenses.length === 0
      ) {
        return null;
      }

      return {
        key: location._id,
        label: location.name,
        children: (
          <div style={{ padding: "20px 0" }}>
            {/* Fixed Monthly Cost */}
            {(matchingDefaultValues.length > 0 || locationFixedExpenses.length > 0) && (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <DollarOutlined style={{ fontSize: 16, color: "#52c41a" }} />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#262626",
                    }}
                  >
                    {t("Fixed monthly cost")}
                  </span>
                </div>
                {matchingDefaultValues.length === 1 ? (
                  // Single month - show with start date
                  <div
                    style={{
                      background: "#f6ffed",
                      padding: "12px 16px",
                      borderRadius: 6,
                      border: "1px solid #b7eb8f",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#52c41a",
                        marginBottom: 4,
                      }}
                    >
                      {matchingDefaultValues[0].defaultValue.toLocaleString()}{" "}
                      <span style={{ fontSize: 16, fontWeight: 500 }}>RSD</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "#52c41a",
                      }}
                    >
                      <CalendarOutlined />
                      {t("From")}:{" "}
                      {formatDate(matchingDefaultValues[0].startsAt)}
                    </div>
                  </div>
                ) : (
                  // Multiple months - show aggregated sum with breakdown
                  <div>
                    <div
                      style={{
                        background: "#f6ffed",
                        padding: "12px 16px",
                        borderRadius: 6,
                        border: "1px solid #b7eb8f",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: "#52c41a",
                          marginBottom: 2,
                        }}
                      >
                        {t("Total")} ({matchingDefaultValues.length}{" "}
                        {t("months")})
                      </div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          color: "#52c41a",
                        }}
                      >
                        {matchingDefaultValues
                          .reduce((sum, dv) => sum + (dv.defaultValue || 0), 0)
                          .toLocaleString()}{" "}
                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                          RSD
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        {t("Monthly breakdown")}:
                      </div>
                      {matchingDefaultValues.map((dv, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 12px",
                            background: "#fafafa",
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                        >
                          <span style={{ color: "#595959" }}>
                            {dv.month || formatDate(dv.startsAt)}
                          </span>
                          <span style={{ fontWeight: 600, color: "#262626" }}>
                            {dv.defaultValue.toLocaleString()} RSD
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Generated fixed expenses with payment status */}
                {locationFixedExpenses.length > 0 && (
                  <Space
                    direction="vertical"
                    style={{ width: "100%", marginTop: 12 }}
                    size={8}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#8c8c8c",
                        fontWeight: 600,
                      }}
                    >
                      {t("Payment status by month")}:
                    </div>
                    {locationFixedExpenses.map((fe) => {
                      const monthLabel = `${String(fe.month).padStart(2, "0")}/${fe.year}`;
                      return (
                        <div
                          key={fe._id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 14px",
                            background: "#fafafa",
                            borderRadius: 6,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                color: "#595959",
                                fontWeight: 500,
                              }}
                            >
                              {monthLabel}
                            </span>
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#262626",
                              }}
                            >
                              {fe.amount.toLocaleString()}{" "}
                              <span style={{ fontSize: 12, fontWeight: 500 }}>
                                {fe.currency}
                              </span>
                            </span>
                          </div>
                          <PaymentStatusBadge
                            status={fe.paymentStatus}
                            amount={fe.amount}
                            currency={fe.currency}
                            paidAmount={fe.partialPayment?.paidAmount}
                            remainingAmount={fe.partialPayment?.remainingAmount}
                            onStatusChange={async (status, paidAmount) => {
                              await updateCustomValuePaymentStatus(
                                fe._id,
                                status,
                                paidAmount,
                              );
                              await onRefresh();
                            }}
                            disabled={!canUpdateMatrix}
                          />
                        </div>
                      );
                    })}
                  </Space>
                )}
              </Card>
            )}

            {/* Custom Values (V2) */}
            <Card
              size="small"
              style={{
                marginBottom: 20,
                borderRadius: 8,
                border: "1px solid #e8e8e8",
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FileTextOutlined
                    style={{ fontSize: 16, color: "#1890ff" }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#262626",
                    }}
                  >
                    {t("Additional business expenses")}
                  </span>
                </div>
                {canUpdateMatrix && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ borderRadius: 6 }}
                    onClick={() =>
                      onAddCustomValue({
                        supplierId,
                        supplierName,
                        categoryId,
                        locationId: location._id,
                      })
                    }
                  >
                    {t("Add")}
                  </Button>
                )}
              </div>
              {locationCustomValues.length > 0 ? (
                <Space direction="vertical" style={{ width: "100%" }} size={10}>
                  {locationCustomValues.map((cv) => {
                    // Check if invoice is overdue
                    const isOverdue =
                      cv.paymentStatus !== "paid" &&
                      cv.dueDate &&
                      dayjs(cv.dueDate).isBefore(dayjs(), "day");

                    const isHighlighted = highlightCustomValueId === cv._id;
                    return (
                      <div
                        key={cv._id}
                        ref={isHighlighted ? highlightRef : undefined}
                        style={{
                          padding: "14px 16px",
                          background: isHighlighted
                            ? "#fffbe6"
                            : isOverdue
                              ? "#fff1f0"
                              : cv.currency === "EUR"
                                ? "#e6f7ff"
                                : "#fafafa",
                          borderRadius: 6,
                          border: isHighlighted
                            ? "2px solid #faad14"
                            : isOverdue
                              ? "2px solid #ff4d4f"
                              : `1px solid ${cv.currency === "EUR" ? "#91d5ff" : "#e8e8e8"}`,
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 2px 8px rgba(0,0,0,0.06)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#262626",
                                marginBottom: 6,
                              }}
                            >
                              {cv.description || t("Custom expense")}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                fontSize: 12,
                                color: "#8c8c8c",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <CalendarOutlined />
                                <span>
                                  {t("Document Date")}:{" "}
                                  {formatDate(cv.invoiceDate)}
                                </span>
                              </div>
                              {cv.dueDate && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    color: isOverdue ? "#ff4d4f" : "#8c8c8c",
                                    fontWeight: isOverdue ? 600 : 400,
                                  }}
                                >
                                  <ClockCircleOutlined />
                                  <span>
                                    {t("Due")}: {formatDate(cv.dueDate)}
                                  </span>
                                </div>
                              )}
                              <PaymentStatusBadge
                                status={cv.paymentStatus}
                                amount={cv.amount}
                                currency={cv.currency}
                                paidAmount={cv.partialPayment?.paidAmount}
                                remainingAmount={
                                  cv.partialPayment?.remainingAmount
                                }
                                onStatusChange={async (status, paidAmount) => {
                                  await updateCustomValuePaymentStatus(
                                    cv._id,
                                    status,
                                    paidAmount,
                                  );
                                  await onRefresh();
                                }}
                                disabled={!canUpdateMatrix}
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 20,
                                fontWeight: 700,
                                color:
                                  cv.currency === "EUR" ? "#1890ff" : "#52c41a",
                              }}
                            >
                              {cv.amount.toLocaleString()}
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  marginLeft: 4,
                                }}
                              >
                                {cv.currency}
                              </span>
                            </div>
                            {canUpdateMatrix && (
                              <div style={{ display: "flex", gap: 4 }}>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  style={{ borderRadius: 4 }}
                                  onClick={() =>
                                    onEditCustomValue(cv, {
                                      supplierId,
                                      supplierName,
                                      categoryId,
                                      locationId: location._id,
                                    })
                                  }
                                />
                                <Popconfirm
                                  title={t("Delete expense")}
                                  description={t("Are you sure you want to delete this expense?")}
                                  onConfirm={() => onDeleteCustomValue(cv._id)}
                                  okText={t("Delete")}
                                  cancelText={t("Cancel")}
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    style={{ borderRadius: 4 }}
                                  />
                                </Popconfirm>
                              </div>
                            )}
                          </div>
                        </div>
                        {isOverdue && (
                          <div
                            style={{
                              marginTop: 12,
                              padding: "8px 12px",
                              background: "#fff2e8",
                              border: "1px solid #ffbb96",
                              borderRadius: 4,
                              fontSize: 12,
                              color: "#d4380d",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <ClockCircleOutlined />
                            <span>{t("Payment overdue - Not paid")}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Space>
              ) : (
                <div
                  style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "#bfbfbf",
                    fontSize: 13,
                    background: "#fafafa",
                    borderRadius: 6,
                  }}
                >
                  <FileTextOutlined
                    style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}
                  />
                  <div>{t("No custom expenses for this location")}</div>
                </div>
              )}
            </Card>

            {/* Daily Revenue Expenses (V2) */}
            {locationDailyExpenses.length > 0 && (
              <Card
                size="small"
                style={{
                  marginBottom: 20,
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  <DollarOutlined style={{ fontSize: 16, color: "#722ed1" }} />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#262626",
                    }}
                  >
                    {t("From Daily Revenue")}
                  </span>
                </div>
                <Space direction="vertical" style={{ width: "100%" }} size={10}>
                  {locationDailyExpenses.map((exp, idx) => (
                    <div
                      key={`${exp._id || idx}`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 16px",
                        background: "#f9f0ff",
                        borderRadius: 6,
                        border: "1px solid #d3adf7",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#262626",
                            marginBottom: 6,
                          }}
                        >
                          {exp.description || t("Daily expense")}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            fontSize: 12,
                            color: "#722ed1",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <CalendarOutlined />
                            {formatDate(exp.date)}
                          </div>
                          <PaymentStatusBadge
                            status={exp.paymentStatus}
                            amount={exp.amount}
                            currency="RSD"
                            paidAmount={exp.partialPayment?.paidAmount}
                            remainingAmount={
                              exp.partialPayment?.remainingAmount
                            }
                            onStatusChange={async (status, paidAmount) => {
                              await updateDailyRevenueExpensePaymentStatus(
                                exp._id,
                                status,
                                paidAmount,
                              );
                              await onRefresh();
                            }}
                            disabled={!canUpdateMatrix}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: "#722ed1",
                        }}
                      >
                        {exp.amount.toLocaleString()}
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginLeft: 4,
                          }}
                        >
                          RSD
                        </span>
                      </div>
                    </div>
                  ))}
                </Space>
              </Card>
            )}

            {/* Totals Summary */}
            <Card
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 12,
                border: "none",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.25)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.8)",
                  marginBottom: 8,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "1px",
                }}
              >
                {t("Total")}
              </div>
              <Row gutter={16}>
                {totalRsd > 0 && (
                  <Col xs={24} sm={totalEur > 0 ? 12 : 24}>
                    <div
                      style={{
                        padding: "16px",
                        background: "rgba(255,255,255,0.95)",
                        borderRadius: 8,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 36,
                          fontWeight: 700,
                          color: "#52c41a",
                          lineHeight: 1,
                          marginBottom: 4,
                        }}
                      >
                        {totalRsd.toLocaleString()}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#52c41a",
                          fontWeight: 600,
                        }}
                      >
                        RSD
                      </div>
                    </div>
                  </Col>
                )}
                {totalEur > 0 && (
                  <Col xs={24} sm={totalRsd > 0 ? 12 : 24}>
                    <div
                      style={{
                        padding: "16px",
                        background: "rgba(255,255,255,0.95)",
                        borderRadius: 8,
                        textAlign: "center",
                        marginTop: totalRsd > 0 ? 0 : 0,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 36,
                          fontWeight: 700,
                          color: "#1890ff",
                          lineHeight: 1,
                          marginBottom: 4,
                        }}
                      >
                        {totalEur.toLocaleString()}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#1890ff",
                          fontWeight: 600,
                        }}
                      >
                        EUR
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>
          </div>
        ),
      };
    })
    .filter(Boolean);

  if (locationTabs.length === 0) {
    return (
      <div style={{ padding: 16, textAlign: "center", color: "#8c8c8c" }}>
        {t("No expenses for this supplier")}
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#f5f5f5",
        padding: "8px 20px 20px",
        borderRadius: 8,
      }}
    >
      <Tabs items={locationTabs as any} />
    </div>
  );
};
